import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Play, Pin, Filter, Sparkles, Plus, Loader2, Clock } from "lucide-react";
import axios from "axios";
import { supabase } from "@/lib/supabase";
import { useDashboardData } from "../../hooks/useDashboardData";
import { AccentDot } from "../components/Shared";
import { getBackendUrl } from "@/lib/utils";

const BACKEND = getBackendUrl();
const API = `${BACKEND}/api`;

const CATEGORIES = ["all", "grammar", "vocabulary", "pronunciation", "goal", "scene", "confidence", "voice"];

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-panel/10 ${className}`} />
);

// ─── Build graph nodes from real memory_json ──────────────────────────────────

function memoryToGraphNodes(memory) {
  const nodes = [];

  (memory.failed_vocabulary || []).forEach((v, i) => {
    nodes.push({
      id: `failed_${i}`,
      label: v.term,
      category: "vocabulary",
      accent: "violet",
      strength: 0.3,
      detail: `Struggling with this term. ${v.last_failed ? `Last failed ${new Date(v.last_failed).toLocaleDateString()}.` : ""}`,
      recommendation: "Practice using it in a sentence during your next live session.",
      tags: ["vocabulary", "needs-work", "active"],
      cefr: memory.cefr_level || "A1",
      savedAt: v.last_failed,
    });
  });

  (memory.mastered_vocabulary || []).forEach((v, i) => {
    nodes.push({
      id: `mastered_${i}`,
      label: v.term,
      category: "vocabulary",
      accent: "cyan",
      strength: 0.85,
      detail: `Mastered. ${v.mastered_on ? `Cemented on ${new Date(v.mastered_on).toLocaleDateString()}.` : "AURA is confident you know this."}`,
      recommendation: "Keep using it naturally — try it in new contexts.",
      tags: ["vocabulary", "mastered"],
      cefr: memory.cefr_level || "A1",
      savedAt: v.mastered_on,
    });
  });

  (memory.strengths || []).forEach((s, i) => {
    nodes.push({
      id: `strength_${i}`,
      label: s.length > 28 ? s.slice(0, 28) + "…" : s,
      category: "grammar",
      accent: "blue",
      strength: 0.75,
      detail: s,
      recommendation: "A consistent strength — keep expressing it naturally.",
      tags: ["grammar", "strength"],
      cefr: memory.cefr_level || "A1",
      savedAt: null,
    });
  });

  (memory.working_on || []).filter(w => w !== "Getting started").forEach((w, i) => {
    nodes.push({
      id: `working_${i}`,
      label: w.length > 28 ? w.slice(0, 28) + "…" : w,
      category: "confidence",
      accent: "blue",
      strength: 0.5,
      detail: `An active focus area: "${w}". AURA will steer sessions toward this.`,
      recommendation: "Make it a theme in your next conversation.",
      tags: ["focus", "active"],
      cefr: memory.cefr_level || "A1",
      savedAt: null,
    });
  });

  (memory.personal_context || []).forEach((p, i) => {
    nodes.push({
      id: `ctx_${i}`,
      label: p.fact.length > 28 ? p.fact.slice(0, 28) + "…" : p.fact,
      category: "goal",
      accent: "violet",
      strength: 0.65,
      detail: `${p.fact}${p.first_mentioned ? ` (remembered since ${new Date(p.first_mentioned).toLocaleDateString()})` : ""}`,
      recommendation: "AURA uses this to personalise every session.",
      tags: ["personal", "context"],
      cefr: memory.cefr_level || "A1",
      savedAt: p.first_mentioned,
    });
  });

  if (memory.next_focus && memory.next_focus !== "Your first conversation with AURA") {
    nodes.push({
      id: "next_focus",
      label: memory.next_focus.length > 28 ? memory.next_focus.slice(0, 28) + "…" : memory.next_focus,
      category: "scene",
      accent: "cyan",
      strength: 0.9,
      detail: `Current session focus: "${memory.next_focus}". This is what AURA will prioritise next.`,
      recommendation: "Start a session — AURA will open with this.",
      tags: ["focus", "next-session"],
      cefr: memory.cefr_level || "A1",
      savedAt: null,
    });
  }

  return nodes;
}

// Generate simple edges between semantically close nodes
function buildEdges(nodes) {
  const edges = [];
  nodes.forEach((a, i) => {
    nodes.slice(i + 1).forEach((b, j) => {
      if (a.category === b.category) {
        edges.push([a.id, b.id]);
      }
    });
  });
  return edges.slice(0, nodes.length * 2); // cap edges
}

// ─── MemoryGraph ──────────────────────────────────────────────────────────────

const MemoryGraph = () => {
  const { memory, sessionStats, isLoading, isNewUser } = useDashboardData();
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 520 });
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  // composer state
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [extra, setExtra] = useState([]);

  useEffect(() => {
    const onResize = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      setSize({ w: r.width, h: Math.max(420, r.width * 0.62) });
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [text]);

  // Build nodes from real memory
  const graphNodes = useMemo(() => [
    ...memoryToGraphNodes(memory),
    // Locally added nodes (from composer responses)
    ...extra.map((e, i) => ({
      id: `extra_${i}`,
      label: e.label || e.category || "New memory",
      category: e.category || "vocabulary",
      accent: "cyan",
      strength: 0.5,
      detail: e.detail || text,
      recommendation: "AURA will weave this into your next session.",
      tags: [e.category || "manual"],
      cefr: memory.cefr_level || "A1",
      savedAt: "just now",
    })),
  ], [memory, extra, text]);

  const graphEdges = useMemo(() => buildEdges(graphNodes), [graphNodes]);

  // Layout — deterministic force-ish layout
  const positions = useMemo(() => {
    const W = size.w, H = size.h;
    const cx = W / 2, cy = H / 2;
    const cats = ["grammar", "vocabulary", "pronunciation", "goal", "scene", "confidence", "voice"];
    const groupAngle = (cat) => (cats.indexOf(cat) / cats.length) * Math.PI * 2 - Math.PI / 2;
    const map = {};
    graphNodes.forEach((n, i) => {
      const ga = groupAngle(n.category);
      const r = 120 + (1 - n.strength) * 110 + (i % 3) * 18;
      const jitter = (Math.sin(i * 13.37) * 0.5 + Math.cos(i * 7.7) * 0.5) * 0.18;
      const a = ga + jitter;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r * 0.78;
      map[n.id] = { x, y };
    });
    return map;
  }, [size, graphNodes]);

  const filtered = graphNodes.filter((n) => {
    if (filter !== "all" && n.category !== filter) return false;
    if (query.trim() && !n.label.toLowerCase().includes(query.trim().toLowerCase())) return false;
    return true;
  });
  const filteredIds = new Set(filtered.map((n) => n.id));

  const onSubmitMemory = async (e) => {
    e?.preventDefault?.();
    const value = text.trim();
    if (!value) return;
    setSaving(true);
    try {
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await axios.post(`${API}/memory/add`, { text: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExtra((prev) => [{ ...res.data, saved_at: "just now" }, ...prev].slice(0, 6));
      setText("");
    } catch {
      // Graceful fallback: add the node locally even without a server response
      setExtra((prev) => [{ id: `local_${Date.now()}`, label: value.slice(0, 30), category: "vocabulary" }, ...prev].slice(0, 6));
      setText("");
    } finally {
      setSaving(false);
    }
  };

  // ─── Empty state for new users ────────────────────────────────────────────

  if (!isLoading && isNewUser && graphNodes.length === 0) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="page-memory-graph">
        <div className="mt-16 rounded-3xl glass p-12 text-center">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">memory graph · empty</div>
          <h2 className="mt-3 font-display text-[26px] font-light text-ink">No memories yet.</h2>
          <p className="mt-2 text-whisper text-[14px] max-w-md mx-auto">
            After your first session, AURA will populate this graph with everything she's learned about you — your vocabulary gaps, strengths, and personal context.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3">
            {/* Composer still available for new users */}
            <form onSubmit={onSubmitMemory} className="w-full max-w-md glass rounded-2xl p-4 edge-glow">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mb-3">
                <Sparkles size={12} className="text-aura-cyan" /> add manually
              </div>
              <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="A mistake, a goal, a word…"
                className="w-full bg-panel/[0.02] border border-panel/10 rounded-xl p-3 text-[13.5px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/60 resize-none"
                data-testid="memory-composer-input"
              />
              <button type="submit" disabled={saving || !text.trim()} className="mt-3 btn-primary py-2 px-4 text-[10px] w-full justify-center disabled:opacity-50" data-testid="memory-composer-submit">
                {saving ? <><Loader2 size={11} className="animate-spin" /> saving</> : <><Plus size={11} /> save memory</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="page-memory-graph">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-panel/10 bg-elev/60 min-w-[260px] flex-1 max-w-md" data-testid="memory-search">
          <Search size={14} className="text-whisper-muted" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search memory…" className="bg-transparent outline-none text-[13.5px] text-ink w-full" />
          {query && <button onClick={() => setQuery("")} aria-label="Clear"><X size={12} className="text-whisper-muted" /></button>}
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="memory-filters">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted hidden sm:inline-flex items-center gap-1.5"><Filter size={12} /> filter</span>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`pill text-[10px] ${filter === c ? "" : "border-panel/10 bg-panel/[0.02] text-whisper"}`} data-testid={`memory-filter-${c}`}>{c}</button>
          ))}
        </div>
      </div>

      {/* Graph + side */}
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Graph canvas */}
        <div ref={wrapRef} className="lg:col-span-8 relative rounded-3xl glass edge-glow overflow-hidden" style={{ height: size.h + 40 }} data-testid="memory-canvas">
          <div aria-hidden className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-aura-cyan/10 blur-[140px]" />
          <div aria-hidden className="absolute -bottom-24 -left-24 w-[260px] h-[260px] rounded-full bg-aura-violet/10 blur-[140px]" />

          {/* Stats top */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted z-10">
            <span>{filtered.length} of {graphNodes.length} nodes</span>
            <span>{graphEdges.length} synapses · live</span>
          </div>

          {isLoading ? (
            <div className="absolute inset-0 grid place-items-center">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted animate-pulse">loading memory…</div>
            </div>
          ) : (
            <svg viewBox={`0 0 ${size.w} ${size.h + 40}`} className="block w-full h-full" preserveAspectRatio="xMidYMid meet">
              <defs>
                <radialGradient id="memNode" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgb(var(--accent-cyan-rgb))" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="rgb(var(--accent-cyan-rgb))" stopOpacity="0" />
                </radialGradient>
              </defs>
              {/* Edges */}
              {graphEdges.map(([a, b], i) => {
                const pa = positions[a]; const pb = positions[b];
                if (!pa || !pb) return null;
                const dim = !filteredIds.has(a) || !filteredIds.has(b);
                return (
                  <line key={i} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
                    stroke={dim ? "rgb(var(--panel-rgb) / 0.06)" : "rgb(var(--accent-cyan-rgb) / 0.35)"}
                    strokeWidth={dim ? 0.6 : 1}
                    style={{ transition: "stroke 220ms ease" }}
                  />
                );
              })}
              {/* Nodes */}
              {graphNodes.map((n) => {
                const p = positions[n.id]; if (!p) return null;
                const visible = filteredIds.has(n.id);
                const accentVar = `rgb(var(--accent-${n.accent}-rgb))`;
                const r = 8 + n.strength * 12;
                const isSelected = selected?.id === n.id;
                return (
                  <g key={n.id} style={{ cursor: "pointer", opacity: visible ? 1 : 0.18, transition: "opacity 220ms ease" }} onClick={() => setSelected(n)}>
                    <circle cx={p.x} cy={p.y} r={r * 2.2} fill={accentVar} opacity={visible ? 0.18 : 0.04} />
                    <circle cx={p.x} cy={p.y} r={r} fill={accentVar} opacity={0.92} style={{ filter: "drop-shadow(0 0 10px " + accentVar + ")" }} />
                    {isSelected && <circle cx={p.x} cy={p.y} r={r + 6} fill="none" stroke={accentVar} strokeWidth="1.5" />}
                    <text x={p.x} y={p.y + r + 14} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="0.16em" fill="rgb(var(--whisper-rgb))" style={{ textTransform: "uppercase" }}>
                      {n.label}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}

          {/* Mini legend */}
          <div className="absolute bottom-4 left-4 flex items-center gap-3 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted z-10">
            <span className="flex items-center gap-1.5"><AccentDot accent="cyan" /> mastered</span>
            <span className="flex items-center gap-1.5"><AccentDot accent="blue" /> working on</span>
            <span className="flex items-center gap-1.5"><AccentDot accent="violet" /> needs work</span>
          </div>
        </div>

        {/* Side: detail drawer + composer */}
        <div className="lg:col-span-4 space-y-4">
          {/* Detail card */}
          <AnimatePresence mode="wait">
            {selected ? (
              <motion.div key={selected.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3 }}
                className="node-card p-5" data-testid="memory-detail"
              >
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                    <AccentDot accent={selected.accent} /> {selected.category}
                  </div>
                  <button onClick={() => setSelected(null)} aria-label="Close"><X size={14} className="text-whisper-muted hover:text-ink" /></button>
                </div>
                <h3 className="relative z-10 mt-3 font-display text-[22px] font-light tracking-tight text-ink leading-tight">{selected.label}</h3>
                <p className="relative z-10 mt-2 text-whisper text-[13.5px] leading-[1.55]">{selected.detail}</p>
                {selected.savedAt && (
                  <div className="relative z-10 mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-whisper-muted">
                    <Clock size={10} />
                    {new Date(selected.savedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                )}
                <div className="relative z-10 mt-4 pt-3 border-t border-panel/8">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-aura-cyan">next</div>
                  <div className="text-[13px] text-ink/90 mt-1">{selected.recommendation}</div>
                </div>
                <div className="relative z-10 mt-4 flex flex-wrap gap-1.5">
                  {(selected.tags || []).map((t) => (<span key={t} className="text-[10px] font-mono tracking-[0.08em] px-2 py-1 rounded-full border border-panel/10 text-whisper-muted">#{t}</span>))}
                </div>
                <div className="relative z-10 mt-5 flex gap-2">
                  <button className="btn-primary py-2 px-3 text-[10px]"><Play size={11} /> replay</button>
                  <button className="btn-ghost py-2 px-3 text-[10px]"><Pin size={11} /> pin</button>
                </div>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hairline rounded-3xl p-6 text-center" data-testid="memory-detail-empty">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">tap a node</div>
                <div className="mt-2 font-serif italic text-[18px] text-ink/85">to open a memory</div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Composer — calls real POST /api/memory/add */}
          <form onSubmit={onSubmitMemory} className="glass rounded-3xl p-5 edge-glow" data-testid="memory-composer">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
              <Sparkles size={12} className="text-aura-cyan" /> add a memory
            </div>
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="A mistake, a goal, a word…"
              className="mt-3 w-full bg-panel/[0.02] border border-panel/10 rounded-2xl p-3 text-[13.5px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/60 focus:ring-[3px] focus:ring-aura-cyan/10 resize-none"
              data-testid="memory-composer-input"
            />
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-whisper-muted"><span className="kbd">⌘</span> <span className="kbd">↵</span></span>
              <button type="submit" disabled={saving || !text.trim()} className="btn-primary py-2 px-3 text-[10px] disabled:opacity-50" data-testid="memory-composer-submit">
                {saving ? <><Loader2 size={11} className="animate-spin" /> saving</> : <><Plus size={11} /> save</>}
              </button>
            </div>
            <AnimatePresence>
              {extra.map((m) => (
                <motion.div key={m.id || `extra_${m.label || m}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-3 hairline rounded-2xl p-3" data-testid="memory-extra">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-aura-cyan">{m.category || "memory"}</div>
                  <div className="mt-1 font-display text-[14px] text-ink">{m.label || m}</div>
                </motion.div>
              ))}
            </AnimatePresence>
          </form>
        </div>
      </div>

      {/* Recent memories list */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="memory-recent">
        {isLoading ? (
          Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)
        ) : graphNodes.length === 0 ? null : (
          graphNodes.slice(0, 8).map((n) => (
            <button key={n.id} onClick={() => setSelected(n)} className="text-left node-card p-4">
              <div className="relative z-10 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                <AccentDot accent={n.accent} /> {n.category}
              </div>
              <div className="relative z-10 mt-2 font-display text-[15px] text-ink leading-tight">{n.label}</div>
              <div className="relative z-10 mt-1 text-whisper text-[12.5px] line-clamp-1">{n.detail}</div>
              {n.savedAt && (
                <div className="relative z-10 mt-2 flex items-center gap-1 font-mono text-[9.5px] tracking-[0.18em] uppercase text-whisper-muted">
                  <Clock size={9} /> {new Date(n.savedAt).toLocaleDateString()}
                </div>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default MemoryGraph;
